// shell-init.js — SPA Shell 启动初始化
// 桌面/移动双壳：各自只内嵌一个框架模板（desktop-tpl 或 mobile-tpl），
// shell-init 探测当前壳存在的那个模板注入，不再做视口切换。
// 断点 1024px 只用于 spa-entry/router 的平台重定向（见各文件）。
// 移动壳统一顶栏：HEADER_CONF 配置表驱动标题与按钮显隐，交互统一走事件派发。

import { get as getSessionCache } from '@shared/session-cache.js';
import { openProfilePanel, renderAvatarInto } from '@lib/profile-panel.js';

// 页面模板 id 映射（chunkId → 模板 id）
var PAGE_TPL = {
  'home': 'home-tpl',
  'project': 'project-tpl',
  'archive': 'archive-tpl',
  'files': 'files-tpl'
};

// 当前已注入的框架类型：null=未注入，否则为 'desktop' / 'mobile'
var _frameType = null;

// 探测当前壳的框架类型：桌面壳只含 desktop-tpl、移动壳只含 mobile-tpl
function detectFrameType() {
  return document.getElementById('mobile-tpl') ? 'mobile' : 'desktop';
}

// 顶栏配置表（顺序照搬 lumeet：标题 → ＋ → 搜索 → 头像；详情页：返回 → 编辑 → 头像，标题留空由内容区页头展示）
var HEADER_CONF = {
  home:    { title: function() { return '你好，' + username(); }, back: false, plus: false, download: false, search: false, edit: false, ph: '' },
  project: { title: '项目', back: false, plus: true, download: false, search: true, edit: false, ph: '搜日程 / 讲师 / 参会人员…' },
  files:   { title: '文件', back: false, plus: true, download: false, search: true, edit: false, ph: '搜索文件…' },
  archive: { title: '资料', back: false, plus: true, download: true, search: true, edit: false, ph: '搜索专家 / 单位…' },
  detail:  { title: '', back: true, plus: false, download: false, search: false, edit: true, ph: '' }
};

function username() {
  var cache = getSessionCache();
  return (cache && cache.gate && cache.gate.username) || '';
}

// 顶栏 DOM 缓存（框架注入后缓存，节点失效时重建）
var _els = null;
function headerEls() {
  if (_els && _els.header && _els.header.isConnected) return _els;
  _els = {
    header: document.getElementById('mHeader'),
    title: document.getElementById('mTitle'),
    back: document.getElementById('mBack'),
    plus: document.getElementById('mPlus'),
    download: document.getElementById('mDownload'),
    searchBtn: document.getElementById('mSearchBtn'),
    edit: document.getElementById('mEdit'),
    input: document.getElementById('searchInput'),
    clear: document.getElementById('searchClear'),
    avatar: document.getElementById('mAvatar'),
    segRow: document.getElementById('segRow')
  };
  return _els;
}

var _searchTimer = null;

// 项目详情用同一 chunkId（project）+ 独立模板：顶栏按详情页配置
function pageKeyOf(chunkId, params) {
  if (chunkId === 'project' && params && params.id) return 'detail';
  return chunkId;
}

// 页面 key → 模板 id（详情页有独立模板，其余走 PAGE_TPL 映射）
function tplIdOf(key) {
  return key === 'detail' ? 'project-detail-tpl' : (PAGE_TPL[key] || 'home-tpl');
}

// 收起搜索 + 清空待触发的防抖
function collapseSearch() {
  var els = headerEls();
  if (els.header) els.header.classList.remove('searching');
  if (_searchTimer) { clearTimeout(_searchTimer); _searchTimer = null; }
}

// 更新顶栏（标题 + 按钮显隐 + 头像 + 复位搜索 + 分段栏）
export function setMobileHeader(chunkId, params) {
  if (_frameType !== 'mobile') return;
  var els = headerEls();
  if (!els.header) return;
  var conf = HEADER_CONF[chunkId] || HEADER_CONF.home;

  els.title.textContent = typeof conf.title === 'function' ? conf.title() : conf.title;
  els.back.classList.toggle('show', !!conf.back);
  els.plus.classList.toggle('show', !!conf.plus);
  els.download.classList.toggle('show', !!conf.download);
  els.searchBtn.classList.toggle('show', !!conf.search);
  els.edit.classList.toggle('show', !!conf.edit);

  if (els.avatar) renderAvatarInto(els.avatar);

  collapseSearch();
  if (els.input) { els.input.value = ''; els.input.placeholder = conf.ph || '搜索…'; }
  if (els.segRow) els.segRow.classList.toggle('show', chunkId === 'archive');
}

// 顶栏标题兜底回填（详情页加载失败时显示「加载失败」提示）
export function updateHeaderTitle(name) {
  var els = headerEls();
  if (els.title) els.title.textContent = name || '';
}

// 派发搜索关键词（页面 chunk 各自监听）
function dispatchSearch(kw) {
  try { window.dispatchEvent(new CustomEvent('hami:search', { detail: kw || '' })); } catch (e) {}
}

// 派发顶栏动作（＋/下载/编辑/返回，页面 chunk 各自监听）
function dispatchHeaderAction(action) {
  try { window.dispatchEvent(new CustomEvent('hami:header-action', { detail: action })); } catch (e) {}
}

// 顶栏交互（移动壳注入后绑定一次）
function bindMobileHeader() {
  var els = headerEls();
  if (!els.header || els.header._hb) return;
  els.header._hb = true;

  // 搜索框输入：防抖 300ms 派发（正常态与选择态两个搜索框共用）
  function bindSearch(input) {
    input.addEventListener('input', function() {
      if (_searchTimer) clearTimeout(_searchTimer);
      _searchTimer = setTimeout(function() { dispatchSearch(input.value); }, 300);
    });
  }

  // 搜索展开：藏标题/＋/下载/编辑/返回，只留胶囊 + 头像
  els.searchBtn.addEventListener('click', function() {
    els.header.classList.add('searching');
    els.input.focus();
  });
  // 收起搜索 + 清空关键词 + 清空输入框（对齐设计稿：点 × 一并清掉文字）
  els.clear.addEventListener('click', function() {
    collapseSearch();
    if (els.input) els.input.value = '';
    dispatchSearch('');
  });
  bindSearch(els.input);
  // 选择态搜索框（独立选择工具条内）及其清空按钮
  var selInput = document.getElementById('selectSearchInput');
  var selClear = document.getElementById('selectSearchClear');
  if (selInput) bindSearch(selInput);
  if (selClear) selClear.addEventListener('click', function() {
    if (selInput) selInput.value = '';
    dispatchSearch('');
  });
  // 头像 → 个人信息
  els.avatar.addEventListener('click', function() { openProfilePanel(); });
  // 动作按钮 → 事件
  els.back.addEventListener('click', function() { dispatchHeaderAction('back'); });
  els.plus.addEventListener('click', function() { dispatchHeaderAction('new'); });
  els.download.addEventListener('click', function() { dispatchHeaderAction('download'); });
  els.edit.addEventListener('click', function() { dispatchHeaderAction('edit'); });
}

// 注入当前壳的单一框架模板（仅一次，之后路由切换只换 #pageSlot 内容区）
export function ensureShell() {
  var root = document.getElementById('app-root');
  if (!root) return;
  var needType = detectFrameType();
  // 框架已注入且节点仍在 → 复用，不重建
  if (_frameType === needType && root.firstElementChild) return;

  var frameTpl = document.getElementById(needType === 'mobile' ? 'mobile-tpl' : 'desktop-tpl');
  if (!frameTpl) {
    root.innerHTML = '<div class="placeholder">页面模板缺失</div>';
    return;
  }

  root.replaceChildren(frameTpl.content.cloneNode(true));
  _frameType = needType;
  populateUser(needType);
  if (needType === 'mobile') bindMobileHeader();
}

// 填充用户信息 + 头像 + 点头像打开个人信息浮窗（移动端头像在统一顶栏，由 setMobileHeader 渲染）
function populateUser(frameType) {
  var cache = getSessionCache();
  var gate = (cache && cache.gate) || {};
  var username = gate.username || '';
  var role = gate.role || '';
  var isAdmin = role === 'admin';
  var roleText = isAdmin ? '管理员' : (role || '');

  if (frameType === 'desktop') {
    var av = document.getElementById('sideUserAv');
    var nameEl = document.getElementById('sideUserName');
    var roleEl = document.getElementById('sideUserRole');
    if (av) renderAvatarInto(av);
    if (nameEl && username) nameEl.textContent = username;
    if (roleEl) roleEl.textContent = roleText;

    var sideUser = document.getElementById('sideUser');
    if (sideUser) {
      sideUser.classList.add('admin-entry'); // 复用指针/hover 样式（对所有用户可点）
      sideUser.setAttribute('role', 'button');
      sideUser.setAttribute('title', '个人信息');
      sideUser.addEventListener('click', function() { openProfilePanel(); });
    }
  }
}

// 更新 #pageSlot 内容区（只换内容，不重建框架）+ 同步顶栏
export function setPageSlot(chunkId, params) {
  var key = pageKeyOf(chunkId, params);
  var pageTpl = document.getElementById(tplIdOf(key));
  var slot = document.getElementById('pageSlot');
  if (!pageTpl || !slot) return;

  slot.replaceChildren(pageTpl.content.cloneNode(true));

  // 顶栏随页面更新（桌面壳无顶栏，内部自动跳过）
  setMobileHeader(key, params);
}

export function initShell() {
  // 关闭浏览器自带的滚动恢复
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  // 首屏注入一次框架（双壳固定框架，无跨断点重建）
  ensureShell();
}

// 跨 chunk 暴露顶栏标题回填（项目详情页异步加载后用）
window.__hamiHeader = { updateTitle: updateHeaderTitle };
